import { Tag, Space, Button, Image, Tooltip, Popconfirm, Typography, Badge } from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  TagsOutlined,
  StarFilled,
} from "@ant-design/icons";
import { resolveImageUrl, resolveBlogUrl, STORES } from "./stores";

const labelForHash = (hash) =>
  STORES.find((s) => s.hash === hash)?.label || "";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const buildColumns = ({
  onView,
  onDelete,
  onManageKeywords,
  tagCountMap = {},
  page = 1,
  limit = 20,
}) => [
    {
      title: "Sr. No",
      key: "sno",
      width: 70,
      align: "center",
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "Thumb",
      dataIndex: "thumbnailPath",
      key: "thumbnailPath",
      width: 80,
      render: (path, record) => {
        const src = resolveImageUrl(path, {
          storeHash: record.storeHash,
          storeLabel: record.storeLabel,
        });
        return src ? (
          <Image
            src={src}
            width={56}
            height={40}
            style={{ objectFit: "cover", borderRadius: 4 }}
            fallback="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='40'><rect width='100%' height='100%' fill='%23f0f0f0'/></svg>"
          />
        ) : (
          <div
            style={{
              width: 56,
              height: 40,
              background: "#f0f0f0",
              borderRadius: 4,
            }}
          />
        );
      },
    },
    {
      title: "Store",
      key: "store",
      width: 130,
      sorter: (a, b) => {
        const la = a.storeLabel || labelForHash(a.storeHash) || "";
        const lb = b.storeLabel || labelForHash(b.storeHash) || "";
        return la.localeCompare(lb);
      },
      render: (_, record) => {
        const lbl = record.storeLabel || labelForHash(record.storeHash) || "";
        return (
          <Space size={4} direction="vertical" style={{ lineHeight: 1.2 }}>
            {lbl ? (
              <Tag color="geekblue" style={{ marginRight: 0 }}>
                {lbl}
              </Tag>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                —
              </Typography.Text>
            )}
            <Tooltip title={record.storeHash || ""}>
              <Typography.Text code style={{ fontSize: 11 }}>
                {record.storeHash || "—"}
              </Typography.Text>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 360,
      ellipsis: true,
      sorter: (a, b) => String(a.title || "").localeCompare(String(b.title || "")),
      render: (title, record) => {
        const fullUrl = record.blogUrl
          ? resolveBlogUrl(record.blogUrl, {
            storeHash: record.storeHash,
            storeLabel: record.storeLabel,
          })
          : null;
        return (
          <div>
            <Space size={4} wrap={false} align="center">
              <div style={{ fontWeight: 500 }}>
                {fullUrl ? (
                  <Tooltip title={fullUrl}>
                    <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                      {title || "Untitled"}
                    </a>
                  </Tooltip>
                ) : (
                  <Tooltip title={title}>{title || "Untitled"}</Tooltip>
                )}
              </div>
              {Number(record.isDefault) === 1 && (
                <Tag color="gold" icon={<StarFilled />} style={{ marginRight: 0 }}>
                  Default
                </Tag>
              )}
            </Space>
            {record.blogUrl && (
              <div style={{ fontSize: 12, color: "#888" }}>
                <Tooltip title={record.blogUrl}>{record.blogUrl}</Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
      width: 160,
      sorter: (a, b) => String(a.author || "").localeCompare(String(b.author || "")),
    },
    {
      title: "Published date",
      dataIndex: "publishedDate",
      key: "publishedDate",
      width: 130,
      sorter: (a, b) => {
        const ta = a.publishedDate ? new Date(a.publishedDate).getTime() : 0;
        const tb = b.publishedDate ? new Date(b.publishedDate).getTime() : 0;
        return ta - tb;
      },
      render: formatDate,
    },
    {
      title: "Published",
      dataIndex: "isPublished",
      key: "isPublished",
      width: 90,
      align: "center",
      sorter: (a, b) => Number(!!a.isPublished) - Number(!!b.isPublished),
      render: (v) =>
        v ? <Tag color="green">Yes</Tag> : <Tag color="gold">No</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      fixed: "right",
      align: "center",
      render: (_, record) => {
        const tagCount = tagCountMap[String(record.id)] ?? record.keyword_points?.length ?? 0;
        return (
          <Space>
           
            <Tooltip title="Manage keyword points">
              <Badge count={tagCount} size="small" offset={[-2, 2]}>
                <Button
                  size="small"
                  icon={<TagsOutlined />}
                  onClick={() => onManageKeywords?.(record)}
                >
                  Tags
                </Button>
              </Badge>
            </Tooltip>
             <Tooltip title="View">
              <Button size="small" icon={<EyeOutlined />} onClick={() => onView?.(record)} />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

export default buildColumns;
